import type { ChatViewMode } from 'components/ChatLauncher/ChatDockContext';
import { FULLSCREEN_CHAT_MIN_WIDTH } from 'components/AIChat/fullscreenChatLayout';

export interface PanelPosition {
  left: number;
  top: number;
}

export interface PanelSize {
  width: number;
  height: number;
}

export interface PanelRect extends PanelPosition, PanelSize {}

export type PanelPlacement = 'bottom' | 'top' | 'left' | 'right';

interface PanelGeometryOptions {
  viewMode: ChatViewMode;
  viewport: PanelSize;
  drawerWidth: number;
  anchor: PanelRect | null;
  placement: PanelPlacement;
  floatingPosition: PanelPosition | null;
  rightPadding: number;
}

const EDGE_PADDING = 8;

export function clampPanelPosition(
  position: PanelPosition,
  size: PanelSize,
  viewport: PanelSize,
  rightPadding: number,
): PanelPosition {
  return {
    left: Math.max(EDGE_PADDING, Math.min(position.left, viewport.width - size.width - rightPadding)),
    top: Math.max(EDGE_PADDING, Math.min(position.top, viewport.height - size.height - EDGE_PADDING)),
  };
}

export function getChatPanelRect({
  viewMode,
  viewport,
  drawerWidth,
  anchor,
  placement,
  floatingPosition,
  rightPadding,
}: PanelGeometryOptions): PanelRect {
  if (viewMode === 'docked') {
    const width = Math.min(drawerWidth, viewport.width);
    return { left: viewport.width - width, top: 0, width, height: viewport.height };
  }
  if (viewMode === 'expanded') {
    const width = Math.min(
      viewport.width - 16,
      Math.max(FULLSCREEN_CHAT_MIN_WIDTH, Math.min(viewport.width * 0.85, 1100)),
    );
    const height = Math.min(viewport.height * 0.9, 900);
    return { left: (viewport.width - width) / 2, top: (viewport.height - height) / 2, width, height };
  }

  const size = { width: Math.min(500, viewport.width - rightPadding - EDGE_PADDING), height: viewport.height * 0.8 };
  let position = floatingPosition ?? {
    left: viewport.width - size.width - rightPadding,
    top: viewport.height - size.height - EDGE_PADDING,
  };
  if (!floatingPosition && anchor) {
    const above = anchor.top - size.height;
    const below = anchor.top + anchor.height;
    const left = anchor.left - size.width;
    const right = anchor.left + anchor.width;
    const centered = {
      left: anchor.left + (anchor.width - size.width) / 2,
      top: anchor.top + (anchor.height - size.height) / 2,
    };
    if (placement === 'top' || placement === 'bottom') {
      const fitsAbove = above >= EDGE_PADDING;
      const fitsBelow = below + size.height <= viewport.height - EDGE_PADDING;
      position = {
        left: centered.left,
        top: placement === 'top' ? (fitsAbove || !fitsBelow ? above : below) : fitsBelow || !fitsAbove ? below : above,
      };
    } else {
      const fitsLeft = left >= EDGE_PADDING;
      const fitsRight = right + size.width <= viewport.width - rightPadding;
      position = {
        top: centered.top,
        left: placement === 'left' ? (fitsLeft || !fitsRight ? left : right) : fitsRight || !fitsLeft ? right : left,
      };
    }
  }
  return { ...clampPanelPosition(position, size, viewport, rightPadding), ...size };
}
